/**
 * Manpower P&L — Workers + Salary API service.
 *
 * Types come from the OpenAPI-generated client (src/api/generated/manpower-workers.ts,
 * built from GET /v1/api/manpower-contract/workers). Envelope is { status, statusCode,
 * message, data }; JWT + city scoping handled by the shared axios client.
 *
 * Note the read/write asymmetry the contract documents:
 *   - SalaryCard.monthly_salary / ot_rate_per_hour come back as STRINGS (decimals).
 *   - SetSalaryBody.monthly_salary / ot_rate_per_hour are sent as NUMBERS.
 * Salary is effective-dated: you don't mutate a salary, you upsert a new card.
 */
import { apiService } from './api';
import type { components } from '../api/generated/manpower-workers';

export type Worker = components['schemas']['Worker'];
export type SalaryCard = components['schemas']['SalaryCard'];
export type AddWorkerBody = components['schemas']['AddWorkerBody'];
export type UpdateWorkerBody = components['schemas']['UpdateWorkerBody'];
export type SetSalaryBody = components['schemas']['SetSalaryBody'];

export interface ListWorkersResponse {
	status: boolean;
	statusCode: number;
	message: string;
	data: Worker[];
}

export interface ListWorkersParams {
	/** Filter by vendor (the worker→vendor mapping). */
	vendorId?: number;
	/** Filter by the worker's default facility. */
	facilityId?: number;
}

export class ManpowerWorkersApiService {
	/** GET /workers — list workers, optionally filtered by vendor / facility. */
	static async listWorkers(params?: ListWorkersParams): Promise<ListWorkersResponse> {
		const sp = new URLSearchParams();
		if (params?.vendorId) sp.set('vendorId', params.vendorId.toString());
		if (params?.facilityId) sp.set('facilityId', params.facilityId.toString());
		const q = sp.toString();
		const response = await apiService.get<ListWorkersResponse>(`/manpower/workers${q ? `?${q}` : ''}`);
		return response as unknown as ListWorkersResponse;
	}

	/** POST /vendors/{vendorId}/workers — add (map) a worker to a vendor. */
	static async addWorkerToVendor(vendorId: number, body: AddWorkerBody): Promise<{ id?: number }> {
		const response = await apiService.post<{ data: { id?: number } }>(
			`/manpower/vendors/${vendorId}/workers`,
			body
		);
		return (response as unknown as { data?: { id?: number } }).data ?? {};
	}

	/** PATCH /workers/{workerId} — update a worker (category, facility, status, shift). */
	static async updateWorker(workerId: number, body: UpdateWorkerBody): Promise<void> {
		await apiService.patch(`/manpower/workers/${workerId}`, body);
	}

	/** GET /workers/{workerId}/salary?asOf=YYYY-MM-DD — active salary card as-of a date (or null). */
	static async getSalary(workerId: number, asOf: string): Promise<SalaryCard | null> {
		const response = await apiService.get<{ data: SalaryCard | null }>(
			`/manpower/workers/${workerId}/salary?asOf=${encodeURIComponent(asOf)}`
		);
		return (response as unknown as { data: SalaryCard | null }).data ?? null;
	}

	/** POST /workers/{workerId}/salary — set (upsert) an effective-dated salary card. */
	static async setSalary(workerId: number, body: SetSalaryBody): Promise<{ id?: number }> {
		const response = await apiService.post<{ data: { id?: number } }>(
			`/manpower/workers/${workerId}/salary`,
			body
		);
		return (response as unknown as { data?: { id?: number } }).data ?? {};
	}
}
